import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { auth, db } from '../firebase';
import { createUserWithEmailAndPassword, signInWithEmailAndPassword, updateProfile, sendPasswordResetEmail } from 'firebase/auth';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { backendConfig } from '../config/backendConfig';
import { useNavigate } from 'react-router-dom';
import AccountTypeSelector from './AccountTypeSelector';
import PasswordInput from './PasswordInput';
import { authFetch } from '../utils/authFetch';

function authRoleCardClass(active) {
  return `auth-premium-role-card p-2 sm:p-3 rounded-xl transition-all duration-200 w-full${active ? ' auth-premium-role-card--active' : ''}`;
}

function authRoleDotClass(active) {
  return `w-2 h-2 sm:w-3 sm:h-3 rounded-full flex-shrink-0 ${active ? 'auth-premium-role-dot--active' : 'auth-premium-role-dot'}`;
}

const authInputClass =
  'auth-premium-input px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base min-h-[44px]';

// navigation support for redirecting signup to new flow

export default function AuthModal({
  isOpen,
  onClose,
  mode = 'login',
  onModeSwitch,
  /** When true (e.g. home “Join”), send users to full-page `/signup`. Must be false for login gates (ProtectedRoute) or signup mode wrongly redirects after session expiry. */
  redirectSignupToFullPage = false,
}) {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [newsletter, setNewsletter] = useState(true);
  const [isMentorLogin, setIsMentorLogin] = useState(false);
  const [loginType, setLoginType] = useState('student'); // 'student', 'organization', or 'mentor'
  const [mentorEmail, setMentorEmail] = useState('');
  const [mentorCode, setMentorCode] = useState('');
  // New dynamic fields for signup
  const [accountType, setAccountType] = useState('student');
  const [dob, setDob] = useState('');
  const [parentEmail, setParentEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [showMembershipExpired, setShowMembershipExpired] = useState(false);
  const [primaryOwnerEmail, setPrimaryOwnerEmail] = useState('');
  const [showAccountSelector, setShowAccountSelector] = useState(false);
  const [loginEmail, setLoginEmail] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [forgotPasswordSuccess, setForgotPasswordSuccess] = useState(false);
  const { login } = useAuth();

  // Check if email is already a primary ID with timeout
  const checkIfPrimaryId = async (email) => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
      
      const response = await fetch(`${backendConfig.baseUrl}/api/membership-public/check-primary-id?email=${encodeURIComponent(email)}`, {
        signal: controller.signal
      });
      clearTimeout(timeoutId);
      
      if (response.ok) {
        const data = await response.json();
        return data.isPrimaryId || false;
      }
      return false;
    } catch (error) {
      // Silently handle errors - if backend is down, assume not primary ID and let Firebase auth handle it
      if (error.name === 'AbortError' || error.message?.includes('Load failed') || error.message?.includes('Failed to fetch')) {
        console.warn('Backend unavailable for primary ID check, proceeding with Firebase auth');
      } else {
        console.error('Error checking primary ID:', error);
      }
      return false;
    }
  };

  // Check account type for better error messages with timeout
  const checkAccountType = async (email) => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
      
      const response = await fetch(`${backendConfig.baseUrl}/api/membership-public/check-email-account-type?email=${encodeURIComponent(email)}&accountType=mentor&mentorType=organization`, {
        signal: controller.signal
      });
      clearTimeout(timeoutId);
      
      if (response.ok) {
        const data = await response.json();
        return data;
      }
      return { emailExists: false, canProceed: true };
    } catch (error) {
      // Silently handle errors - if backend is down, return default values
      if (error.name === 'AbortError' || error.message?.includes('Load failed') || error.message?.includes('Failed to fetch') || error.message?.includes('access control')) {
        console.warn('Backend unavailable for account type check, using default values');
      } else {
        console.error('Error checking account type:', error);
      }
      return { emailExists: false, canProceed: true };
    }
  };

  const handleNotifyPrimaryOwner = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${backendConfig.baseUrl}/api/mentors/notify-renewal`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          mentorId: `${mentorEmail}_mentor`, // This will be updated to use actual ownerId
          mentorEmail: mentorEmail
        })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.ok) {
          setSuccess('Notification sent to your organization owner. They will receive an email to renew the plan.');
          setShowMembershipExpired(false);
        } else {
          setError('Failed to send notification. Please try again.');
        }
      } else {
        setError('Failed to send notification. Please try again.');
      }
    } catch (error) {
      console.error('Error sending notification:', error);
      setError('Failed to send notification. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const age = React.useMemo(() => {
    if (!dob) return null;
    const d = new Date(dob);
    if (Number.isNaN(d.getTime())) return null;
    const diff = Date.now() - d.getTime();
    return new Date(diff).getUTCFullYear() - 1970;
  }, [dob]);
  const isUnder13 = accountType === 'student' && age !== null && age < 13;

  // Optional: marketing flows open full-page signup. Login gates must not auto-navigate here (see ProtectedRoute).
  React.useEffect(() => {
    if (isOpen && mode === 'signup' && redirectSignupToFullPage) {
      if (onClose) onClose();
      navigate('/signup');
    }
  }, [isOpen, mode, onClose, navigate, redirectSignupToFullPage]);

  // Reset loading state when modal closes
  React.useEffect(() => {
    if (!isOpen) {
      setLoading(false);
      setError('');
      setSuccess('');
    }
  }, [isOpen]);

  // Aggressive loading state monitor - automatically clear loading after 25 seconds
  React.useEffect(() => {
    if (loading && isOpen) {
      const forceClearLoading = setTimeout(() => {
        console.warn('⚠️ Loading state force-cleared after 25 seconds (modal was open)');
        setLoading(false);
        setError('Request timed out. Please try again.');
      }, 25000);
      
      return () => {
        clearTimeout(forceClearLoading);
      };
    }
  }, [loading, isOpen]);

  if (!isOpen) return null;

  const handleModeSwitch = (newMode) => {
    setError('');
    setSuccess('');
    setEmail('');
    setPassword('');
    setName('');
    setConfirmPassword('');
    if (onModeSwitch) {
      onModeSwitch(newMode);
    }
  };

  const shouldGoToMembership = async (user) => {
    // Short-circuit if recently activated membership to avoid redirect loops
    try {
      const ts = parseInt(localStorage.getItem('membershipJustActivatedAt') || '0', 10);
      if (ts && (Date.now() - ts) < 60000) return { needsMembership: false, isRenewal: false };
    } catch (_) {}
    try {
      // Production-robust rule:
      // redirect to membership ONLY on explicit backend verdict.
      const res = await authFetch(`${backendConfig.baseUrl}/api/membership/status?uid=${user.uid}`);
      if (!res.ok) {
        // Unknown/transient status -> do not force membership here.
        return { needsMembership: false, isRenewal: false };
      }
      const j = await res.json().catch(() => ({}));
      if (j?.allowed === true) return { needsMembership: false, isRenewal: false };
      if (j?.isNewAccount === true) return { needsMembership: true, isRenewal: false };
      const m = j?.membership || {};
      const isExpired = m?.isExpired === true || m?.status === 'expired' || m?.status === 'cancelled' || m?.status === 'past_due';
      if (isExpired) return { needsMembership: true, isRenewal: true };
      // No explicit backend verdict: allow and let ProtectedRoute re-check.
      return { needsMembership: false, isRenewal: false };
    } catch (_) {
      // On timeout/abort/network errors, do not force membership from login modal.
      return { needsMembership: false, isRenewal: false };
    }
  };

  const checkUserProfile = async (user) => {
    try {
      // First, enforce membership selection if needed
      const membershipCheck = await shouldGoToMembership(user);
      if (membershipCheck.needsMembership) {
        // Navigate to membership page with renewal state
        navigate('/membership', { 
          state: { 
            isRenewal: membershipCheck.isRenewal 
          } 
        });
        return;
      }
      const profileRef = doc(db, "artistProfiles", user.uid);
      const profileSnap = await getDoc(profileRef);
      
      // Let AccountTypeRouter handle the routing based on user's account type
      navigate('/');
    } catch (error) {
      console.error('Error checking profile:', error);
      // Fallback to dashboard if there's an error
      navigate('/dashboard');
    }
  };

  async function handleSubmit(e) {
    e.preventDefault();
    if (loading) return; // prevent double submits

    setError('');
    setSuccess('');
    setLoading(true);
    
    // Safety timeout - ensure loading is always cleared after 20 seconds
    // Defined outside try block so it's accessible in all catch blocks
    let safetyTimeout = setTimeout(() => {
      console.warn('Login safety timeout - forcing loading to false (this is normal if connection is slow)');
      setLoading(false);
      setError('Login request took too long. Please check your connection and try again.');
    }, 20000);
    
    // Helper function to clear safety timeout
    const clearSafetyTimeout = () => {
      if (safetyTimeout) {
        clearTimeout(safetyTimeout);
        safetyTimeout = null;
      }
    };

    try {
      
      if (loginType === 'mentor' && mode === 'login') {
        // Mentor access uses email + (access code OR password)
        if (!mentorEmail || (!mentorCode && !password)) {
          setError('Please enter your mentor email and either access code or password.');
          setLoading(false);
          return;
        }
        const normMentor = mentorEmail.trim().toLowerCase();
        
        // Check if user provided password (single mentor login)
        if (password && !mentorCode) {
          // Single mentor login with password
          try {
            const userCredential = await signInWithEmailAndPassword(auth, normMentor, password);
            const user = userCredential.user;
            
            // Check if this is actually a single mentor account
            const userDoc = await getDoc(doc(db, 'users', user.uid));
            if (userDoc.exists()) {
              const userData = userDoc.data();
              if (userData.accountType === 'mentor' && userData.mentorType === 'single') {
                // Single mentor - login and redirect to mentor dashboard
                clearSafetyTimeout();
                setLoading(false);
                setSuccess('Single mentor login successful! Redirecting...');
                
                // Close modal first
                try {
                  onClose();
                } catch (e) {
                  console.warn('Error closing modal:', e);
                }
                
                // Login and navigate - don't await, let it happen in background
                login(user).then(() => {
                  setTimeout(() => {
                    navigate('/mentor-dashboard');
                  }, 100);
                }).catch((loginError) => {
                  console.error('Error during login:', loginError);
                  // Still navigate even if login fails
                  navigate('/mentor-dashboard');
                });
              } else {
                // Not a single mentor account
                await auth.signOut();
                setError('This email is not registered as a single mentor account. Please use the correct login option.');
              }
            } else {
              await auth.signOut();
              setError('Account not found. Please check your credentials.');
            }
          } catch (error) {
            console.error('Single mentor login error:', error);
            if (error.code === 'auth/user-not-found') {
              setError('No single mentor account found with this email.');
            } else if (error.code === 'auth/wrong-password') {
              setError('Incorrect password. Please try again.');
            } else if (error.code === 'auth/invalid-email') {
              setError('Invalid email address.');
            } else {
              setError('Login failed. Please check your credentials and try again.');
            }
          } finally {
            setLoading(false);
          }
          return;
        }
        
        // Organization mentor login with access code
        try {
          // Strict validation-first with small retry, no bypass
          const attempt = async () => {
            const controller = new AbortController();
            const timeout = setTimeout(() => controller.abort(), 4000);
            try {
              const resp = await fetch(`${backendConfig.baseUrl}/api/mentors/validate`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: normMentor, code: mentorCode }), signal: controller.signal
              });
              clearTimeout(timeout);
              const j = await resp.json().catch(()=>null);
              return { resp, j };
            } catch (e) {
              clearTimeout(timeout);
              return { resp: null, j: null };
            }
          };

          // up to 2 retries
          let result = await attempt();
          if (!(result.resp && result.resp.ok && result.j && result.j.ok)) {
            await new Promise(r => setTimeout(r, 400));
            result = await attempt();
          }

          if (result.resp && result.resp.ok && result.j && result.j.ok) {
            try {
              localStorage.setItem('mentor_session', JSON.stringify({ email: normMentor, ownerId: result.j.ownerId, orgCode: result.j.orgCode, at: Date.now() }));
              window.dispatchEvent(new Event('mentor_session_changed'));
            } catch (_) {}
            setSuccess('Mentor access granted. Redirecting…');
            setTimeout(() => { try { onClose(); } catch(_) {} window.location.href = '/mentor'; }, 250);
          } else {
            // If server responded with an error body, check for specific error cases
            const status = result.resp ? result.resp.status : 0;
            const errorData = result.j;
            
            if (status === 403 && errorData && errorData.error === 'Owner membership expired') {
              // Get primary owner email for notification
              try {
                const mentorData = await fetch(`${backendConfig.baseUrl}/api/mentors/latest?email=${encodeURIComponent(normMentor)}`);
                if (mentorData.ok) {
                  const mentorInfo = await mentorData.json();
                  if (mentorInfo.ok && mentorInfo.ownerId) {
                    // Get primary owner email
                    const ownerResponse = await authFetch(`${backendConfig.baseUrl}/api/membership/status?uid=${mentorInfo.ownerId}`);
                    if (ownerResponse.ok) {
                      const ownerData = await ownerResponse.json();
                      if (ownerData.parentId) {
                        // This is a mentor account, get the parent email
                        const parentResponse = await authFetch(`${backendConfig.baseUrl}/api/membership/status?uid=${ownerData.parentId}`);
                        if (parentResponse.ok) {
                          const parentData = await parentResponse.json();
                          setPrimaryOwnerEmail(parentData.membership?.email || '');
                        }
                      } else {
                        setPrimaryOwnerEmail(ownerData.membership?.email || '');
                      }
                    }
                  }
                }
              } catch (e) {
                console.error('Error fetching primary owner email:', e);
              }
              setShowMembershipExpired(true);
              setError('');
            } else if (status === 401 && errorData && errorData.error === 'Invalid code') {
              setError('Invalid access code. Please check the code sent in your invite email.');
            } else {
            setError(status && status !== 0 ? `Invalid access code (HTTP ${status}).` : 'Network error while validating code. Please try again.');
            }
          }
        } catch (e) {
          console.error('Mentor validate error:', e);
          setError(e && e.message ? e.message : 'Network issue while validating code. Please try again.');
        } finally {
          setLoading(false);
        }
        return;
      }

      if (loginType === 'student' && mode === 'login') {
        // Regular student login with email and password
        if (!email || !password) {
          setError('Please enter your email and password.');
          setLoading(false);
          clearSafetyTimeout();
          return;
        }
        
        // Global timeout for entire login process (20 seconds max)
        const globalTimeout = setTimeout(() => {
          console.warn('Student login: Global timeout reached (this is normal if connection is slow)');
          setLoading(false);
          setError('Login request timed out. Please check your connection and try again.');
        }, 20000);
        
        try {
          // Authenticate with Firebase (with timeout)
          let userCredential;
          try {
            const firebaseAuthPromise = signInWithEmailAndPassword(auth, email, password);
            const firebaseAuthTimeout = new Promise((_, reject) => 
              setTimeout(() => reject(new Error('Firebase auth timeout')), 30000)
            );
            userCredential = await Promise.race([firebaseAuthPromise, firebaseAuthTimeout]);
          } catch (authError) {
            clearTimeout(globalTimeout);
            if (authError.code === 'auth/network-request-failed') {
              console.warn('Firebase network request failed - check internet connection');
              setError('Network error. Please check your internet connection and try again.');
            } else if (authError.code === 'auth/user-not-found') {
              setError('No account found with this email.');
            } else if (authError.code === 'auth/wrong-password') {
              setError('Incorrect password. Please try again.');
            } else if (authError.code === 'auth/invalid-email') {
              setError('Invalid email address.');
            } else if (authError.message === 'Firebase auth timeout') {
              console.warn('Firebase auth timeout - this may be due to slow connection');
              setError('Authentication request timed out. Please check your connection and try again.');
            } else if (authError.message !== 'Firebase auth timeout') {
              console.warn('Firebase authentication error:', authError);
              setError('Login failed. Please check your credentials and try again.');
            } else {
              setError('Login failed. Please check your credentials and try again.');
            }
            setLoading(false);
            return;
          }
          
          const user = userCredential.user;
          
          // Check if this is actually a student account (not a mentor) with timeout
          try {
            const userDocPromise = getDoc(doc(db, 'users', user.uid));
            const userDocTimeout = new Promise((_, reject) => 
              setTimeout(() => reject(new Error('User doc check timeout')), 5000)
            );
            const userDoc = await Promise.race([userDocPromise, userDocTimeout]);
            
            console.log('🔍 Student login - User document check:', {
              exists: userDoc.exists(),
              data: userDoc.exists() ? userDoc.data() : null
            });
            
            if (userDoc.exists()) {
              const userData = userDoc.data();
              if (userData.accountType === 'mentor') {
                // This is a mentor account, not a student account
                clearTimeout(globalTimeout);
                clearSafetyTimeout();
                await auth.signOut();
                setError('This email is registered as a mentor account. Please use the "Art Mentor" login option instead.');
                setLoading(false);
                return;
              }
            }
          } catch (docError) {
            console.warn('User document check failed or timed out, proceeding with login:', docError);
            // Continue with login if document check fails
          }
          
          clearTimeout(globalTimeout);
          
          // Valid student account (or no document exists yet) - always redirect to artist wall
          console.log('✅ Student login successful - redirecting to artist wall');
          clearSafetyTimeout();
          setLoading(false);
          setSuccess('Login successful! Redirecting...');
          
          // Close modal first
          try {
            onClose();
          } catch (e) {
            console.warn('Error closing modal:', e);
          }
          
          // Login and navigate - don't await, let it happen in background
          login(user).then(() => {
            setTimeout(() => {
              // Default post-login: Artist corner (my-art-world). Users with profile stay there; users without profile are redirected to artist-profile by DashboardLanding, then after creation go to Artist corner.
              console.log('🚀 Navigating to Artist corner (my-art-world) after login');
              window.location.href = '/my-art-world';
            }, 100);
          }).catch((loginError) => {
            console.error('Error during login:', loginError);
            // Still navigate even if login fails - auth state is already set
            window.location.href = '/my-art-world';
          });
          clearSafetyTimeout();
          
        } catch (error) {
          clearTimeout(globalTimeout);
          clearSafetyTimeout();
          console.error('Student login error:', error);
          if (error.code === 'auth/user-not-found') {
            setError('No account found with this email.');
          } else if (error.code === 'auth/wrong-password') {
            setError('Incorrect password. Please try again.');
          } else if (error.code === 'auth/invalid-email') {
            setError('Invalid email address.');
          } else {
            setError('Login failed. Please check your credentials and try again.');
          }
          setLoading(false);
        }
        return;
      }

      if (loginType === 'organization' && mode === 'login') {
        // Organization login with email and password
        if (!email || !password) {
          setError('Please enter your organization email and password.');
          setLoading(false);
          return;
        }
        
        // Global timeout for entire login process (30 seconds max)
        const globalTimeout = setTimeout(() => {
          console.warn('Organization login: Global timeout reached (this is normal if connection is slow)');
          setLoading(false);
          setError('Login request timed out. Please check your connection and try again.');
        }, 30000);
        
        try {
          // Step 1: Check if this email exists and is an organization account (with timeout)
          let isOrgAccount = false;
          try {
            const primaryIdPromise = checkIfPrimaryId(email);
            const primaryIdTimeout = new Promise((_, reject) => 
              setTimeout(() => reject(new Error('Primary ID check timeout')), 8000)
            );
            isOrgAccount = await Promise.race([primaryIdPromise, primaryIdTimeout]);
          } catch (checkError) {
            console.warn('Primary ID check failed or timed out, proceeding with Firebase auth:', checkError);
            // Continue with Firebase auth if check fails
          }
          
          if (!isOrgAccount) {
            // Step 2: Get more specific information about the account type (with timeout)
            let accountInfo = { emailExists: false, canProceed: true };
            try {
              const accountTypePromise = checkAccountType(email);
              const accountTypeTimeout = new Promise((_, reject) => 
                setTimeout(() => reject(new Error('Account type check timeout')), 8000)
              );
              accountInfo = await Promise.race([accountTypePromise, accountTypeTimeout]);
              console.log('AuthModal - Account type check result:', accountInfo);
            } catch (checkError) {
              console.warn('Account type check failed or timed out, proceeding with Firebase auth:', checkError);
              // Continue with Firebase auth if check fails
            }
            
            // Only show errors if we got valid account info from backend
            if (accountInfo.emailExists && !accountInfo.canProceed) {
              clearTimeout(globalTimeout);
              clearSafetyTimeout();
              setError('This email is registered as a different account type. Please use the correct login option (Student, Art Mentor, or Organization) or contact support.');
              setLoading(false);
              return;
            } else if (accountInfo.emailExists) {
              clearTimeout(globalTimeout);
              clearSafetyTimeout();
              setError('This email is not registered as an organization account. Please check your email or contact your organization administrator.');
              setLoading(false);
              return;
            }
            // If emailExists is false, backend might be down, so proceed with Firebase auth
          }

          // Step 3: Authenticate with Firebase (with timeout)
          let userCredential;
          try {
            const firebaseAuthPromise = signInWithEmailAndPassword(auth, email, password);
            const firebaseAuthTimeout = new Promise((_, reject) => 
              setTimeout(() => reject(new Error('Firebase auth timeout')), 30000)
            );
            userCredential = await Promise.race([firebaseAuthPromise, firebaseAuthTimeout]);
          } catch (authError) {
            clearTimeout(globalTimeout);
            clearSafetyTimeout();
            if (authError.code === 'auth/network-request-failed') {
              console.warn('Firebase network request failed - check internet connection');
              setError('Network error. Please check your internet connection and try again.');
            } else if (authError.code === 'auth/user-not-found') {
              setError('No organization account found with this email. Please sign up to create an organization account.');
              setTimeout(() => {
                setError('');
                setLoginType('organization');
                if (onModeSwitch) onModeSwitch('signup');
              }, 2000);
            } else if (authError.code === 'auth/wrong-password') {
              setError('Incorrect password. Please try again.');
            } else if (authError.code === 'auth/invalid-email') {
              setError('Invalid email address.');
            } else if (authError.code === 'auth/too-many-requests') {
              setError('Too many failed login attempts. Please try again later.');
            } else if (authError.message === 'Firebase auth timeout') {
              console.warn('Firebase auth timeout - this may be due to slow connection');
              setError('Authentication request timed out. Please check your connection and try again.');
            } else {
              console.warn('Firebase authentication error:', authError);
              setError('Login failed. Please check your credentials and try again.');
            }
            setLoading(false);
            return;
          }
          
          const user = userCredential.user;
          
          // Step 4: Check membership status (with timeout)
          try {
            const membershipController = new AbortController();
            const membershipTimeoutId = setTimeout(() => membershipController.abort(), 10000); // 10 second timeout
            
            const membershipPromise = authFetch(`${backendConfig.baseUrl}/api/membership/status?uid=${user.uid}`, {
              signal: membershipController.signal
            });
            
            const membershipResponse = await Promise.race([
              membershipPromise,
              new Promise((_, reject) => 
                setTimeout(() => {
                  membershipController.abort();
                  reject(new Error('Membership check timeout'));
                }, 10000)
              )
            ]);
            
            clearTimeout(membershipTimeoutId);
            clearTimeout(globalTimeout);
            
            if (membershipResponse.ok) {
              const membershipData = await membershipResponse.json();
              console.log('Organization login - Membership data:', membershipData);
              
              const membership = membershipData.membership || {};
              const isActive = membership.isActive;
              const isExpired = membership.isExpired;
              
              console.log('Organization login - Status check:', {
                isActive: isActive,
                isExpired: isExpired,
                membership: membership
              });
              
              if (isActive && !isExpired) {
                // Membership is active, authenticate and redirect
                clearSafetyTimeout();
                setLoading(false);
                setSuccess('Organization login successful! Redirecting...');
                
                // Close modal first
                try {
                  onClose();
                } catch (e) {
                  console.warn('Error closing modal:', e);
                }
                
                // Login and navigate - don't await, let it happen in background
                login(user).then(() => {
                  setTimeout(() => {
                    navigate('/organization-dashboard');
                  }, 100);
                }).catch((loginError) => {
                  console.error('Error during login:', loginError);
                  // Still navigate even if login fails
                  navigate('/organization-dashboard');
                });
              } else {
                // Membership expired - sign out and redirect to renewal
                clearSafetyTimeout();
                setLoading(false);
                await auth.signOut();
                setError('Your organization membership has expired. Please renew your membership to continue.');
                setTimeout(() => {
                  try {
                    onClose();
                    navigate('/organization-renewal');
                  } catch (e) {
                    console.error('Error during redirect:', e);
                    window.location.href = '/organization-renewal';
                  }
                }, 2000);
              }
            } else {
              // Can't verify membership - sign out
              clearSafetyTimeout();
              setLoading(false);
              try {
                await auth.signOut();
              } catch (signOutError) {
                console.error('Error signing out:', signOutError);
              }
              setError('Unable to verify membership status. Please contact support or try again.');
            }
          } catch (membershipError) {
            clearTimeout(globalTimeout);
            console.error('Membership check error:', membershipError);
            
            // If membership check fails, still allow login but log the error
            // This prevents blocking users if backend is temporarily down
            if (membershipError.name === 'AbortError' || membershipError.message === 'Membership check timeout') {
              console.warn('Membership check timed out, allowing login to proceed');
              clearSafetyTimeout();
              setLoading(false);
              setSuccess('Login successful! Redirecting...');
              
              // Close modal first
              try {
                onClose();
              } catch (e) {
                console.warn('Error closing modal:', e);
              }
              
              // Allow login to proceed - membership check can happen later
              // Don't await, let it happen in background
              login(user).then(() => {
                setTimeout(() => {
                  navigate('/organization-dashboard');
                }, 100);
              }).catch((loginError) => {
                console.error('Error during login:', loginError);
                // Still navigate even if login fails
                navigate('/organization-dashboard');
              });
            } else {
              // For other errors, sign out and show error
              clearSafetyTimeout();
              setLoading(false);
              try {
                await auth.signOut();
              } catch (signOutError) {
                console.error('Error signing out:', signOutError);
              }
              setError('Unable to verify membership status. Please try again or contact support.');
            }
          }
          
        } catch (error) {
          clearTimeout(globalTimeout);
          clearSafetyTimeout();
          console.error('Organization login error:', error);
          if (error.code === 'auth/user-not-found') {
            setError('No organization account found with this email. Please sign up to create an organization account.');
            setTimeout(() => {
              setError('');
              setLoginType('organization');
              if (onModeSwitch) onModeSwitch('signup');
            }, 2000);
          } else if (error.code === 'auth/wrong-password') {
            setError('Incorrect password. Please try again.');
          } else if (error.code === 'auth/invalid-email') {
            setError('Invalid email address.');
          } else if (error.code === 'auth/too-many-requests') {
            setError('Too many failed login attempts. Please try again later.');
          } else {
            setError('Login failed. Please check your credentials and try again.');
          }
          setLoading(false);
        }
        return;
      }

      if (mode === 'signup') {
        if (password !== confirmPassword) {
          return setError('Passwords do not match. Please try again.');
        }
        if (password.length < 6) {
          return setError('Password must be at least 6 characters long.');
        }
        if (accountType === 'student' && !dob) {
          return setError('Please enter your date of birth.');
        }
        if (isUnder13 && !parentEmail) {
          return setError('Parent/guardian email is required for students under 13.');
        }
        
        // Check if email is already a primary ID for organization accounts
        if (accountType === 'ngo_admin') {
          const isPrimaryId = await checkIfPrimaryId(email);
          if (isPrimaryId) {
            return setError('This email is already registered as a primary organization account. Please login to your organization account instead.');
          }
        }
        
        // Create new user with Firebase
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        const user = userCredential.user;
        
        // Update display name
        await updateProfile(user, {
          displayName: name
        });
        
        // Login with the new user
        await login(user);
        
        setSuccess('Account created successfully! Welcome to Artmosphere!');
        
        try {
          localStorage.setItem('signup_accountType', accountType);
          localStorage.setItem('signup_isUnder13', String(!!isUnder13));
          if (parentEmail) localStorage.setItem('signup_parentEmail', parentEmail);
        } catch (_) {}
        
        // Close modal and navigate after a short delay
        setTimeout(() => {
        onClose();
        navigate('/dashboard');
        }, 1500);
        
      } else {
        // Handle login with Firebase
        try {
          const userCredential = await signInWithEmailAndPassword(auth, email, password);
          const user = userCredential.user;
          
          // Check if user has multiple accounts
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            
            // Check if there are other accounts with the same email
            const usersRef = db.collection('users');
            const snapshot = await usersRef.where('email', '==', email).get();
            
            if (snapshot.size > 1) {
              // User has multiple accounts - show account selector
              setLoginEmail(email);
              setShowAccountSelector(true);
              setLoading(false);
              return;
            }
          }
          
          // Login with the user
          await login(user);
          
          setSuccess('Welcome back! Signing you in...');
          
          // Close modal and check profile after a short delay
          setTimeout(async () => {
        onClose();
            await checkUserProfile(user);
          }, 1500);
          
        } catch (firebaseError) {
          if (firebaseError.code === 'auth/user-not-found') {
            setError('No account found with this email. Please check your email or create a new account.');
          } else if (firebaseError.code === 'auth/wrong-password') {
            setError('Incorrect password. Please try again.');
          } else if (firebaseError.code === 'auth/too-many-requests') {
            setError('Too many failed attempts. Please try again later.');
          } else if (firebaseError.code === 'auth/user-disabled') {
            setError('This account has been disabled. Please contact support.');
          } else if (firebaseError.code === 'auth/invalid-email') {
            setError('Please enter a valid email address.');
          } else {
            setError('Unable to sign in. Please check your details and try again.');
          }
        }
      }
    } catch (error) {
      if (mode === 'signup') {
        if (error.code === 'auth/email-already-in-use') {
          setError('An account with this email already exists. Please try signing in instead.');
        } else if (error.code === 'auth/weak-password') {
          setError('Password is too weak. Please choose a stronger password.');
        } else if (error.code === 'auth/invalid-email') {
          setError('Please enter a valid email address.');
        } else if (error.code === 'auth/operation-not-allowed') {
          setError('Email/password accounts are not enabled. Please contact support.');
        } else {
          setError('Unable to create account. Please check your details and try again.');
        }
      } else {
        setError('Unable to sign in. Please check your details and try again.');
      }
    }
    setLoading(false);
  }

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotPasswordEmail) {
      setError('Please enter your email address.');
      return;
    }
    
    setForgotPasswordLoading(true);
    setError('');
    setForgotPasswordSuccess(false);
    
    try {
      // Configure action URL to use our custom reset password page
      // Use the production URL for action URL to ensure it works
      const actionUrl = window.location.hostname === 'localhost' 
        ? 'https://artmosphere-89b64.web.app/reset-password'
        : `${window.location.origin}/reset-password`;
      
      const actionCodeSettings = {
        url: actionUrl,
        handleCodeInApp: false, // Set to false to use the URL directly
      };
      
      console.log('📧 [AuthModal] Sending password reset email to:', forgotPasswordEmail);
      console.log('🔗 [AuthModal] Action URL:', actionCodeSettings.url);
      console.log('🌐 [AuthModal] Current origin:', window.location.origin);
      console.log('🌐 [AuthModal] Current hostname:', window.location.hostname);
      
      await sendPasswordResetEmail(auth, forgotPasswordEmail, actionCodeSettings);
      
      console.log('✅ [AuthModal] Password reset email sent successfully');
      console.log('📬 [AuthModal] Email should arrive within 2-5 minutes. Check spam folder if not received.');
      setForgotPasswordSuccess(true);
      setError('');
    } catch (error) {
      console.error('❌ [AuthModal] Password reset error:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      
      if (error.code === 'auth/user-not-found') {
        setError('No account found with this email address. Please check your email or sign up for a new account.');
      } else if (error.code === 'auth/invalid-email') {
        setError('Please enter a valid email address.');
      } else if (error.code === 'auth/too-many-requests') {
        setError('Too many password reset requests. Please try again later.');
      } else if (error.code === 'auth/invalid-continue-uri') {
        setError('Invalid redirect URL. Please contact support.');
      } else if (error.code === 'auth/unauthorized-continue-uri') {
        setError('Unauthorized redirect URL. Please contact support.');
      } else {
        setError(`Failed to send password reset email: ${error.message || 'Unknown error'}. Please try again or contact support.`);
      }
    } finally {
      setForgotPasswordLoading(false);
    }
  };


  return (
    <div
      className="auth-premium-backdrop fixed inset-0 flex items-center justify-center z-[100] p-0 sm:p-4 overflow-auto"
      style={{ paddingTop: 'max(env(safe-area-inset-top, 0px), 0px)' }}
    >
      <div
        className="auth-premium-shell flex h-full w-full max-w-5xl flex-col overflow-y-auto sm:h-auto"
        style={{ paddingTop: 'max(calc(0.25rem + env(safe-area-inset-top, 0px)), 0.25rem)' }}
      >
        <div className="auth-premium-gold-rule" aria-hidden />
        <div className="relative z-10 w-full p-4 sm:p-6 md:p-8">
          <div className="mb-4 flex w-full items-start justify-between gap-2 sm:mb-6 md:mb-8">
            <div className="min-w-0 flex-1">
              <p className="auth-premium-eyebrow">
                {mode === 'signup' ? 'Join Community' : 'Sign In'}
              </p>
              <h2 className="auth-premium-title mt-1 text-xl sm:text-2xl md:text-3xl">
                {mode === 'signup' ? 'Join Artmosphere' : 'Welcome Back'}
              </h2>
              <p className="auth-premium-subtitle mt-1 text-xs sm:text-sm">
                {mode === 'signup' ? 'Create your artistic journey' : 'Continue your creative adventure'}
              </p>
            </div>
            <button
              type="button"
              onClick={() => { try { onClose && onClose(); } catch (_) {} }}
              className="auth-premium-close flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-xl sm:h-10 sm:w-10 sm:text-2xl"
              aria-label="Close"
            >
              ✕
            </button>
          </div>

        {mode === 'signup' && (
          <div className="auth-premium-callout mb-6 w-full p-4 backdrop-blur-sm">
            <div className="flex items-center gap-3">
              <div className="auth-premium-callout-icon">
                <span className="text-sm">🎨</span>
              </div>
              <p className="text-sm font-medium text-[#5c4428]">
                Join thousands of young artists creating amazing art together!
              </p>
            </div>
          </div>
        )}
        
        {error && (
          <div className="mb-6 p-4 bg-red-50/80 border border-red-200/50 rounded-2xl w-full backdrop-blur-sm">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <div className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center">
                  <svg className="h-4 w-4 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                </div>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-700 font-medium">
            {error}
                </p>
              </div>
            </div>
          </div>
        )}
        
        {success && (
          <div className="mb-6 p-4 bg-green-50/80 border border-green-200/50 rounded-2xl w-full backdrop-blur-sm">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center">
                  <svg className="h-4 w-4 text-green-500" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                </div>
              </div>
              <div className="ml-3">
                <p className="text-sm text-green-700 font-medium">
                  {success}
                </p>
              </div>
            </div>
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6 w-full">
          {mode === 'signup' ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                <div className="space-y-2">
                  <label htmlFor="name" className="block text-xs sm:text-sm font-semibold text-slate-700">Full Name</label>
                  <input id="name" type="text" required className={authInputClass} value={name} onChange={(e)=>setName(e.target.value)} placeholder="Your artist name" />
                </div>
                <div className="space-y-2">
                  <label htmlFor="email" className="block text-xs sm:text-sm font-semibold text-slate-700">Email address</label>
                  <input id="email" type="email" required className={authInputClass} value={email} onChange={(e)=>setEmail(e.target.value)} placeholder="your.email@example.com" />
                </div>
                <div className="space-y-2">
                  <label htmlFor="password" className="block text-xs sm:text-sm font-semibold text-slate-700">Password</label>
                  <PasswordInput
                    id="password"
                    value={password}
                    onChange={(e)=>setPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    className={authInputClass}
                  />
                  <p className="text-xs text-slate-500 flex items-center gap-1">
                    <span className="w-3 h-3 bg-slate-200 rounded-full flex items-center justify-center text-[8px]">🔒</span>
                    Must be at least 6 characters
                  </p>
            </div>
                <div className="space-y-2">
                  <label htmlFor="confirm-password" className="block text-xs sm:text-sm font-semibold text-slate-700">Confirm Password</label>
                  <PasswordInput
                    id="confirm-password"
                    name="confirmPassword"
                    value={confirmPassword}
                    onChange={(e)=>setConfirmPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    className={authInputClass}
                  />
          </div>
          </div>

              {accountType === 'student' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-slate-700">Date of Birth</label>
                    <input type="date" value={dob} onChange={(e)=>setDob(e.target.value)} className={`${authInputClass} px-4 py-3`} />
                    {age !== null && (
                      <div className="flex items-center gap-2 text-xs">
                        <div className={`w-2 h-2 rounded-full ${age < 13 ? 'bg-amber-400' : 'bg-green-400'}`}></div>
                        <span className="text-slate-600">
                          Age: <span className="font-semibold text-slate-800">{age}</span>
                          {age < 13 && <span className="text-amber-600 ml-1">(Parent consent required)</span>}
                        </span>
                      </div>
                    )}
                  </div>
                  {isUnder13 && (
                    <div className="space-y-2">
                      <label className="block text-sm font-semibold text-slate-700">Parent/Guardian Email</label>
                      <input type="email" value={parentEmail} onChange={(e)=>setParentEmail(e.target.value)} className={`${authInputClass} px-4 py-3`} placeholder="parent@email.com" />
                    </div>
                  )}
            </div>
              )}
            </>
          ) : (
            <>
              {/* Login Type Selection - Horizontal Layout */}
              <div className="space-y-3 mb-4 sm:mb-6">
                <label className="auth-premium-label block text-xs sm:text-sm">I am a...</label>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-3 sm:gap-3">
                  <button
                    type="button"
                    onClick={() => setLoginType('student')}
                    className={authRoleCardClass(loginType === 'student')}
                  >
                    <div className="flex flex-row items-center gap-2 text-center sm:flex-col sm:text-center">
                      <div className="flex items-center gap-2">
                        <div className={authRoleDotClass(loginType === 'student')} />
                        <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
                        </svg>
                      </div>
                      <span className="font-medium text-xs sm:text-xs">Student / Individual Artist</span>
                      <p className="hidden sm:block text-[10px] text-slate-500 leading-tight">Access your personal art journey</p>
                    </div>
                  </button>
                  
                  <button
                    type="button"
                    onClick={() => setLoginType('organization')}
                    className={authRoleCardClass(loginType === 'organization')}
                  >
                    <div className="flex flex-row items-center gap-2 text-center sm:flex-col sm:text-center">
                      <div className="flex items-center gap-2">
                        <div className={authRoleDotClass(loginType === 'organization')} />
                        <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                      </div>
                      <span className="font-medium text-xs sm:text-xs">Organization / School</span>
                      <p className="hidden sm:block text-[10px] text-slate-500 leading-tight">Manage mentors and track progress</p>
                    </div>
                  </button>
                  
                  <button
                    type="button"
                    onClick={() => setLoginType('mentor')}
                    className={authRoleCardClass(loginType === 'mentor')}
                  >
                    <div className="flex flex-row items-center gap-2 text-center sm:flex-col sm:text-center">
                      <div className="flex items-center gap-2">
                        <div className={authRoleDotClass(loginType === 'mentor')} />
                        <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                        </svg>
                      </div>
                      <span className="font-medium text-xs sm:text-xs">Art Mentor</span>
                      <p className="hidden sm:block text-[10px] text-slate-500 leading-tight">Access with organization code</p>
                    </div>
                  </button>
                </div>
              </div>
              {loginType === 'mentor' && (
                <div className="auth-premium-panel mb-4 space-y-3 p-5 shadow-sm">
                  <div>
                    <label className="auth-premium-label mb-1 block text-xs">Mentor email</label>
                    <input type="email" value={mentorEmail} onChange={(e)=>setMentorEmail(e.target.value)} className={`${authInputClass} px-3 py-2`} placeholder="mentor@school.org" required />
                  </div>
                  <div>
                    <label className="auth-premium-label mb-1 block text-xs">Access code</label>
                    <input value={mentorCode} onChange={(e)=>setMentorCode(e.target.value.toUpperCase())} className={`${authInputClass} px-3 py-2 uppercase tracking-widest`} placeholder="ABCDE" />
                  </div>
                  <div className="text-center text-xs font-medium text-[#8b6914]">OR</div>
                  <div>
                    <label className="auth-premium-label mb-1 block text-xs">Password</label>
                    <PasswordInput
                      value={password}
                      onChange={(e)=>setPassword(e.target.value)}
                      className={`${authInputClass} px-3 py-2`}
                      placeholder="Your account password"
                    />
                    <div className="mt-2 text-right">
                      <button
                        type="button"
                        onClick={() => setShowForgotPassword(true)}
                        className="auth-premium-link text-xs"
                      >
                        Forgot password?
                      </button>
                    </div>
                  </div>
                  <div className="text-xs text-[#6b5a48]">
                    Use either the access code sent in your invite OR your account password. Your organization's Co‑Learning Space code is separate.
                  </div>
                </div>
              )}
              {loginType === 'student' && (
                <>
                  <div>
                    <label htmlFor="email" className="auth-premium-label mt-1 block text-xs sm:text-sm">
                      Email address
                    </label>
                    <input
                      id="email"
                      type="email"
                      required
                      className={`${authInputClass} mt-1 block`}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="your.email@example.com"
                      data-testid="login-email"
                    />
                  </div>
                  <div>
                    <label htmlFor="password" className="auth-premium-label block text-xs sm:text-sm">
                      Password
                    </label>
                    <PasswordInput
                      id="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      required
                      className={`${authInputClass} mt-1 block`}
                      inputProps={{ 'data-testid': 'login-password' }}
                    />
                    <div className="mt-2 text-right">
                      <button
                        type="button"
                        onClick={() => setShowForgotPassword(true)}
                        className="auth-premium-link text-xs sm:text-sm"
                      >
                        Forgot password?
                      </button>
                    </div>
                  </div>
                </>
              )}
              
              {loginType === 'organization' && (
                <>
                  <div>
                    <label htmlFor="email" className="auth-premium-label block text-xs sm:text-sm">
                      Organization Email
                    </label>
                    <input
                      id="email"
                      type="email"
                      required
                      className={`${authInputClass} mt-1 block`}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="admin@school.org"
                    />
                  </div>
                  <div>
                    <label htmlFor="password" className="auth-premium-label block text-xs sm:text-sm">
                      Password
                    </label>
                    <PasswordInput
                      id="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      required
                      className={`${authInputClass} mt-1 block`}
                    />
                    <div className="mt-2 text-right">
                      <button
                        type="button"
                        onClick={() => setShowForgotPassword(true)}
                        className="auth-premium-link text-xs sm:text-sm"
                      >
                        Forgot password?
                      </button>
                    </div>
                  </div>
                </>
              )}
            </>
          )}

          {mode === 'signup' && (
            <div className="flex items-start gap-3 p-4 bg-slate-50/80 rounded-xl border border-slate-200/50">
                <input
                  id="newsletter"
                  type="checkbox"
                className="auth-premium-checkbox mt-0.5 h-5 w-5 rounded-md border-slate-300"
                  checked={newsletter}
                  onChange={(e) => setNewsletter(e.target.checked)}
                />
              <label htmlFor="newsletter" className="text-sm text-slate-700 leading-relaxed">
                <span className="font-medium">📧 Stay inspired</span> with Artmosphere updates and creative tips
                </label>
              </div>
          )}
          
          <div className="w-full">
            <button
              type="submit"
              disabled={loading}
              className="art-btn-primary flex min-h-[44px] w-full transform items-center justify-center rounded-xl px-4 py-3 text-xs font-semibold transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50 sm:rounded-2xl sm:px-6 sm:py-4 sm:text-sm"
              data-testid="login-submit"
            >
              {loading ? (
                <div className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-[#5c4428]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {mode === 'signup' ? 'Creating your account...' : 'Signing in...'}
                </div>
              ) : (
                mode === 'signup' ? 'Join Artmosphere' : 
                loginType === 'student' ? 'Sign In' :
                loginType === 'organization' ? 'Access Organization' :
                'Access as Mentor'
              )}
            </button>
          </div>
        </form>
        
        
        <div className="mt-4 w-full text-center sm:mt-6 md:mt-8">
          <p className="auth-premium-subtitle text-xs sm:text-sm">
            {mode === 'signup' ? (
              <>
                Already part of our community?{' '}
                <button
                  type="button"
                  onClick={() => handleModeSwitch('login')}
                  className="auth-premium-link"
                >
                  Sign in here
                </button>
              </>
            ) : (
              <>
                New to Artmosphere?{' '}
                <button
                  type="button"
                  onClick={() => handleModeSwitch('signup')}
                  className="auth-premium-link"
                >
                  Join our community
                </button>
              </>
            )}
          </p>
        </div>
        </div>
      </div>
      
      {/* Membership Expired Popup */}
      {showMembershipExpired && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[200] p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-red-600 text-2xl">⚠️</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Organization Plan Expired</h3>
              <p className="text-gray-600">
                Your organization's membership has expired. Please notify your organization owner to renew the plan.
              </p>
            </div>
            
            <div className="space-y-3">
              <button
                onClick={handleNotifyPrimaryOwner}
                disabled={loading}
                className="art-btn-primary w-full disabled:opacity-50 font-semibold py-3 px-4 rounded-lg"
              >
                {loading ? 'Sending...' : 'Notify Organization Owner'}
              </button>
              <button
                onClick={() => setShowMembershipExpired(false)}
                className="w-full border border-gray-300 text-gray-700 font-semibold py-3 px-4 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Close
              </button>
            </div>
            
            <p className="text-xs text-gray-500 text-center mt-4">
              An email will be sent to your organization owner with renewal instructions.
            </p>
          </div>
        </div>
      )}
      
      {/* Account Type Selector */}
      {showAccountSelector && (
        <AccountTypeSelector 
          email={loginEmail} 
          onClose={() => setShowAccountSelector(false)} 
        />
      )}

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <div className="auth-premium-backdrop fixed inset-0 z-[200] flex items-center justify-center p-4">
          <div className="auth-premium-shell max-w-md w-full overflow-hidden p-0">
            <div className="auth-premium-gold-rule" aria-hidden />
            <div className="p-6">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="auth-premium-title text-xl">Reset Password</h3>
              <button
                type="button"
                onClick={() => {
                  setShowForgotPassword(false);
                  setForgotPasswordEmail('');
                  setError('');
                  setForgotPasswordSuccess(false);
                }}
                className="auth-premium-close flex h-8 w-8 items-center justify-center rounded-full text-xl"
              >
                ✕
              </button>
            </div>
            
            {!forgotPasswordSuccess ? (
              <>
                <p className="text-sm text-gray-600 mb-4">
                  Enter your email address and we'll send you a link to reset your password.
                </p>
                <form onSubmit={handleForgotPassword} className="space-y-4">
                  <div>
                    <label htmlFor="forgot-email" className="block text-sm font-medium text-gray-700 mb-1">
                      Email address
                    </label>
                    <input
                      id="forgot-email"
                      type="email"
                      value={forgotPasswordEmail}
                      onChange={(e) => setForgotPasswordEmail(e.target.value)}
                      className={`${authInputClass} px-3 py-2`}
                      placeholder="your.email@example.com"
                      required
                    />
                  </div>
                  
                  {error && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-sm text-red-700">{error}</p>
                    </div>
                  )}
                  
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => {
                        setShowForgotPassword(false);
                        setForgotPasswordEmail('');
                        setError('');
                      }}
                      className="art-btn-secondary flex-1 rounded-lg px-4 py-2 font-medium"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={forgotPasswordLoading}
                      className="art-btn-primary flex-1 px-4 py-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
                    >
                      {forgotPasswordLoading ? 'Sending...' : 'Send Reset Link'}
                    </button>
                  </div>
                </form>
              </>
            ) : (
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h4 className="text-lg font-semibold text-gray-900 mb-2">Check your email</h4>
                <p className="text-sm text-gray-600 mb-4">
                  We've sent a password reset link to <strong>{forgotPasswordEmail}</strong>
                </p>
                <div className="auth-premium-callout mb-4 p-3 text-left">
                  <p className="mb-1 text-xs font-medium text-[#5c4428]">📧 Didn't receive the email?</p>
                  <ul className="list-inside list-disc space-y-1 text-xs text-[#6b5a48]">
                    <li>Check your spam or junk folder</li>
                    <li>Wait a few minutes - emails can take up to 5 minutes to arrive</li>
                    <li>Make sure you entered the correct email address</li>
                    <li>Check if your email provider is blocking Firebase emails</li>
                  </ul>
                </div>
                <p className="text-xs text-gray-500 mb-4">
                  Click the link in the email to reset your password. The link will expire in 1 hour.
                </p>
                <button
                  onClick={() => {
                    setShowForgotPassword(false);
                    setForgotPasswordEmail('');
                    setForgotPasswordSuccess(false);
                  }}
                  className="art-btn-primary w-full px-4 py-2 rounded-lg font-semibold"
                >
                  Close
                </button>
              </div>
            )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
} 